import { memo, useEffect, useState } from "react";
import {
  formatSafeDecimalInput,
  parseNumber,
  roundToTwo,
} from "../../../../../utils/numberFormatter";
import { InputGroup } from "react-bootstrap";
import NumberInDecimals from "../../../../../components/inputs/NumberFormatted";

const AdjustmentCell = memo(({ row, onActualAmountChange, disabled }) => {
  const sysQty = parseNumber(row.stock_quantity ?? "0");
  const actCountRaw = row.actual_count ?? row.stock_quantity ?? "0";
  const actCount = parseNumber(actCountRaw);

  const derivedDiff = roundToTwo(actCount - sysQty);
  const derivedSign = derivedDiff < 0 ? "-" : "+";
  const derivedAdjValue = Math.abs(derivedDiff);

  const [inputValue, setInputValue] = useState(String(derivedAdjValue));
  const [sign, setSign] = useState(derivedSign);

  useEffect(() => {
    if (parseNumber(inputValue) !== derivedAdjValue) {
      setInputValue(String(derivedAdjValue));
    }
    if (derivedDiff !== 0) {
      setSign(derivedSign);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedAdjValue, derivedSign, derivedDiff]);

  const handleInputChange = (val) => {
    const rawVal = val === "" ? "0" : val;
    setInputValue(rawVal);
    const parsedVal = parseNumber(rawVal);
    const newActCount = sign === "+" ? sysQty + parsedVal : sysQty - parsedVal;
    onActualAmountChange(String(roundToTwo(newActCount)), "actual_count", row);
  };

  const handleToggleSign = () => {
    if (disabled) return; // Prevent toggle if disabled
    const newSign = sign === "+" ? "-" : "+";
    setSign(newSign);
    const parsedVal = parseNumber(inputValue);
    if (parsedVal !== 0) {
      const newActCount =
        newSign === "+" ? sysQty + parsedVal : sysQty - parsedVal;
      onActualAmountChange(
        String(roundToTwo(newActCount)),
        "actual_count",
        row,
      );
    }
  };

  return (
    <InputGroup>
      <InputGroup.Text
        className={`text-white ${sign === "+" ? "bg-success" : "bg-danger"}`}
        onClick={handleToggleSign}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          width: "40px",
          justifyContent: "center",
          opacity: disabled ? 0.6 : 1, // Visually show it is disabled
        }}
      >
        {sign}
      </InputGroup.Text>
      <NumberInDecimals
        className="form-control"
        name="adjustment"
        value={formatSafeDecimalInput(inputValue)}
        onChange={handleInputChange}
        decimals={2}
        required
        disabled={disabled}
      />
    </InputGroup>
  );
});

const IndicatorCell = memo(
  ({ row, fieldName, baseFieldName, onActualAmountChange, disabled }) => {
    const baseRaw = row[baseFieldName] ?? "0";
    const baseValue = parseNumber(baseRaw);
    const currentRaw = row[fieldName] ?? baseRaw;
    const currentValue = parseNumber(currentRaw);

    let symbol = "=";
    let symbolClass = "text-secondary";

    if (currentValue > baseValue) {
      symbol = "↑";
      symbolClass = "text-success fw-bold";
    } else if (currentValue < baseValue) {
      symbol = "↓";
      symbolClass = "text-danger fw-bold";
    }

    return (
      <InputGroup>
        <InputGroup.Text
          className={`bg-light ${symbolClass}`}
          style={{
            width: "40px",
            justifyContent: "center",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {symbol}
        </InputGroup.Text>
        <NumberInDecimals
          className="form-control"
          name={fieldName}
          value={formatSafeDecimalInput(currentRaw)}
          onChange={(val) => {
            const finalVal = val === "" ? "0" : val;
            onActualAmountChange(finalVal, fieldName, row);
          }}
          decimals={2}
          required
          disabled={disabled}
        />
      </InputGroup>
    );
  },
);

export { AdjustmentCell, IndicatorCell };
